import axios, { AxiosError } from 'axios'
import env from 'config/env'
import errorPackage from './error.pkg'
import { STORAGE_KEY } from 'config/const'
import { setSocketHeader } from './socket'
import { refreshAndRetry } from './common'

const request = axios.create({
    baseURL: env.BASE_URL,
})

export class ErrorHandler extends Error {
    constructor(public code: keyof typeof errorPackage, public message: string) {
        super()
    }
}

request.interceptors.request.use(
    function (request) {
        // 로컬 스토리지에서 토큰 가져오기
        // const token = localStorage.getItem(STORAGE_KEY.refreshTokenKey);
        const token = localStorage.getItem(STORAGE_KEY.accessTokenKey)

        // 토큰이 존재하면 Authorization 헤더에 추가
        if (token && request.headers) {
            request.headers['Authorization'] = `Bearer ${token}`
        }

        return request
    },
    function (err) {
        throw new Error(err)
    }
)

request.interceptors.response.use(
    function (response) {
        return response
    },
    async function (err: AxiosError<ErrorData | undefined | null>) {
        const { response, config } = err

        if (!response || !response.data) {
            throw new Error('알 수 없는 오류가 발생했습니다. 다시 시도해주세요.')
        }

        const { code } = response.data

        // 상태 코드를 문자열로 변환
        const statusCode = String(response.status)

        // 타입 가드 함수 정의
        function isErrorKey(key: string): key is keyof typeof errorPackage {
            return key in errorPackage
        }

        // 에러 코드가 없고, 상태 코드에 해당하는 에러 메시지가 있는 경우 에러 throw
        if (!code && isErrorKey(statusCode)) {
            throw new ErrorHandler(statusCode, errorPackage[statusCode])
        }

        const passStatus = [409]

        if (passStatus.includes(response.data.status)) {
            throw response.data
        }

        if (err.message === 'Network Error') {
            throw new Error('알 수 없는 오류가 발생했습니다. 다시 시도해주세요.')
        }

        if (errorPackage[code]) {
            const error = new ErrorHandler(code, errorPackage[code])
            if (code === '000006' && config) {
                try {
                    const retryResponse = await refreshAndRetry(config)
                    return retryResponse
                } catch (e) {
                    localStorage.removeItem(STORAGE_KEY.accessTokenKey)
                    localStorage.removeItem(STORAGE_KEY.refreshTokenKey)
                    return
                }
            }
            throw error
        }

        /**
         *  code: "정의되지 않은 에러입니다. 백엔드 개발자에게 문의해주세요."
         *  error: "No Matched Code"
         *  message: "인증번호가 일치하지 않습니다."
         *  status: 409
         */

        if (response.data.status === 404) {
            return response.data
        }

        throw new Error('알 수 없는 오류가 발생했습니다. 다시 시도해주세요.')
    }
)

export const setHeader = (token: string) => {
    request.defaults.headers.common['Authorization'] = token
    setSocketHeader(token)
}

export interface ErrorData {
    code: keyof typeof errorPackage
    error: string
    message: string
    status: number
}

export interface Response<T = any> {
    data?: T
    message?: string
    status: number
}

export type Meta = {
    isEnd: boolean
    pageableCount: number
    totalCount: number
}

export type Data<T, K = any> = {
    items: T
    meta: Meta
} & K

export interface TodoResponse<T, K = {}> {
    data?: Data<T, K>
    message?: string
    status: number
}

export default request
